import React, { useState } from "react";
import styled from "styled-components";
import { DropdownMulti, colors2, UI_BODY_SM_DF } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
  EmptyBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// DISCORD ICON
// ============================================

export const DiscordIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="6" fill="#5865F2" />
    <path
      fill="#FFFFFF"
      d="M22.34 9.8a16.4 16.4 0 0 0-4.05-1.26.06.06 0 0 0-.06.03c-.18.32-.37.73-.5 1.06a15.16 15.16 0 0 0-4.55 0 10.5 10.5 0 0 0-.51-1.06.06.06 0 0 0-.06-.03A16.36 16.36 0 0 0 8.55 9.8a.05.05 0 0 0-.02.02c-2.58 3.86-3.28 7.62-2.94 11.34 0 .02.01.04.03.05a16.5 16.5 0 0 0 4.96 2.5.06.06 0 0 0 .06-.02c.38-.52.72-1.07 1.02-1.65a.06.06 0 0 0-.03-.08 10.87 10.87 0 0 1-1.55-.74.06.06 0 0 1-.01-.1c.1-.08.21-.16.31-.24a.06.06 0 0 1 .06-.01c3.26 1.49 6.78 1.49 10.01 0a.06.06 0 0 1 .06.01c.1.08.21.16.31.24a.06.06 0 0 1-.01.1c-.5.29-1.02.54-1.55.74a.06.06 0 0 0-.03.08c.3.58.65 1.13 1.02 1.65a.06.06 0 0 0 .06.02 16.45 16.45 0 0 0 4.97-2.5.06.06 0 0 0 .03-.05c.4-4.3-.69-8.04-2.94-11.34a.04.04 0 0 0-.03-.02zM13.04 18.93c-.98 0-1.78-.9-1.78-2 0-1.11.79-2 1.78-2 1 0 1.79.9 1.78 2 0 1.1-.79 2-1.78 2zm5.93 0c-.98 0-1.78-.9-1.78-2 0-1.11.79-2 1.78-2 1 0 1.79.9 1.78 2 0 1.1-.78 2-1.78 2z"
    />
  </svg>
);

const HintLine = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

export const DiscordInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
}) => {
  const {
    connection,
    connectionLoading,
    connectError,
    preflightData,
    preflightError,
    isPreflightLoading,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({ providerId: "discord", providerName: "Discord" });

  const [selectedChannels, setSelectedChannels] = useState([]);

  const submitErrorMessage =
    submitError && submitError.sourceId === "discord" ? submitError.message : null;
  const isEmptyResult =
    submitErrorMessage && /no\s+(discord\s+)?(messages|data)\s+(found|to)/i.test(submitErrorMessage);

  const channels = preflightData?.channels || [];
  const canSubmit = selectedChannels.length > 0;

  const onDisconnect = async () => {
    await handleDisconnect();
    setSelectedChannels([]);
  };

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onClearSubmitError && onClearSubmitError();
    onSubmit({
      adapterOptions: { channelIds: selectedChannels.map((c) => c.value) },
    });
  };

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Discord</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Discord</InterfaceHeader>
        <InterfaceDescription>
          Import messages from a Discord server. You'll be redirected to
          Discord to pick which server to add Dots' bot to, then brought back
          here to choose a channel.
        </InterfaceDescription>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Discord</ButtonPrimary>
        </InterfaceActions>
        <HelpText>
          Dots' bot is read-only — it never posts, replies, or DMs. You'll need
          admin permission on the Discord server to add the bot.
        </HelpText>
      </InterfaceContainer>
    );
  }

  if (connection.status === "stale") {
    // Discord-specific stale messaging — usually the bot was kicked or its
    // permissions changed, not a user-token expiry.
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Discord</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Dots' bot has lost access to "${connection.providerAccountName}". It may have been removed from the server, or its permissions were changed.`
                : "Dots' bot has lost access to your Discord server."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Discord</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Discord</InterfaceHeader>
      <InterfaceDescription>
        Each Discord message becomes one row. Threads under a message are
        flattened into the parent row's <code>thread</code> column.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected to{connection.providerAccountName ? ` ${connection.providerAccountName}` : " your Discord server"}
          </strong>
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>as {connection.adminEmail}</span>
          )}
          {connection.authorizedBy?.name && (
            <span style={{ color: colors2.grey60 }}>
              Authorized by: {connection.authorizedBy.name}
            </span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Discord"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      {preflightError && <ErrorBanner>{preflightError}</ErrorBanner>}
      {submitErrorMessage && isEmptyResult && <EmptyBanner>{submitErrorMessage}</EmptyBanner>}
      {submitErrorMessage && !isEmptyResult && <ErrorBanner>{submitErrorMessage}</ErrorBanner>}

      <div style={{ marginTop: 16 }}>
        <DropdownMulti
          label={adapterConfig?.preflightConfig?.selectLabel || "Select Channels"}
          placeholder={
            isPreflightLoading
              ? "Loading channels..."
              : adapterConfig?.preflightConfig?.selectPlaceholder || "Choose one or more channels to import..."
          }
          options={channels.map((c) => ({
            display: `#${c.name}${c.isPrivate ? " (private)" : ""}`,
            value: c.id,
            _channelInfo: c,
          }))}
          value={selectedChannels}
          onChange={(k, v) => setSelectedChannels(v || [])}
          readOnly={isPreflightLoading || isLoading}
          isSearchable={true}
          isTagsStyle
          searchbarPlaceholder="Search channels..."
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </div>

      <HintLine>
        Heads up: Dots' bot only sees channels it has read permission for. If a
        channel is missing here, check the bot's role permissions in your
        Discord server settings.
      </HintLine>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit || isPreflightLoading || isLoading}
          isLoading={isLoading}
        >
          {adapterConfig?.preflightConfig?.submitLabel || "Import Messages"}
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const discordIntegrationMeta = {
  id: "discord",
  name: "Discord",
  description: "Import messages from a Discord server channel",
};

export default DiscordInterface;
